import { useRef, useState } from "react";
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    CircularProgress,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export default function StepCompanyDetails({ formData, updateData, onNext }) {
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const userInfo = localStorage?.getItem('userInfo');
    const parsedUser = userInfo ? JSON.parse(userInfo) : null;
    const CampanyName = parsedUser?.username ?? null;

    const validate = () => {
        const e = {};
        if (!formData.companyName.trim()) e.companyName = "Company name is required";
        return e;
    };

    const handleNext = () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setErrors({});
        onNext();
    };

    const handleLogoUpload = async (evt) => {
        const file = evt.target.files?.[0];
        if (!file) return;
        updateData({ logoPreview: URL.createObjectURL(file), logoUrl: null });
        setIsUploading(true);
        try {
            // Replace with real upload:
            // const fd = new FormData(); fd.append("file", file);
            // const res = await aiActions.uploadFileS3Bucket(fd);
            // updateData({ logoUrl: res?.data?.data?.url });
            await new Promise((r) => setTimeout(r, 1200));
            updateData({ logoUrl: "uploaded" }); // placeholder
        } catch {
            updateData({ logoUrl: null });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    return (
        <Box>
            {/* Title */}
            <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing="0.1em">
                Step 1 of 4
            </Typography>
            <Typography variant="h5" fontWeight={700} mt={0.5} mb={0.5}>
                Tell us about your{" "}
                <Box component="span" color="primary.main">company</Box>
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                We'll personalise your AI workspace with your brand and context.
            </Typography>

            <Grid container spacing={3}>
                {/* Logo Upload */}
                <Grid item xs={12} sm="auto">
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <Box
                            onClick={() => !isUploading && fileInputRef.current?.click()}
                            sx={{
                                width: 140,
                                height: 140,
                                border: "2px dashed",
                                borderColor: isUploading ? "primary.main" : "grey.300",
                                borderRadius: 3,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: isUploading ? "not-allowed" : "pointer",
                                overflow: "hidden",
                                position: "relative",
                                bgcolor: "grey.50",
                                transition: "border-color 0.2s, bgcolor 0.2s",
                                "&:hover": {
                                    borderColor: "primary.main",
                                    bgcolor: "primary.50",
                                },
                            }}
                        >
                            {isUploading && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "rgba(255,255,255,0.8)",
                                        zIndex: 1,
                                        gap: 1,
                                    }}
                                >
                                    <CircularProgress size={26} />
                                    <Typography variant="caption" color="text.secondary">Uploading…</Typography>
                                </Box>
                            )}

                            {formData.logoPreview ? (
                                <Box
                                    component="img"
                                    src={formData.logoPreview}
                                    alt="Logo"
                                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                            ) : (
                                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                                    <UploadIcon sx={{ color: "grey.400", fontSize: 32 }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                        Upload Logo
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled">
                                        PNG, JPG, SVG
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {formData.logoPreview && !isUploading && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                {formData.logoUrl
                                    ? <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />
                                    : <ErrorIcon sx={{ fontSize: 14, color: "error.main" }} />
                                }
                                <Typography
                                    variant="caption"
                                    color={formData.logoUrl ? "success.main" : "error.main"}
                                    fontWeight={600}
                                >
                                    {formData.logoUrl ? "Uploaded" : "Upload failed"}
                                </Typography>
                            </Box>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleLogoUpload}
                        />
                    </Box>
                </Grid>

                {/* Fields */}
                <Grid item xs={12} sm>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Company Name *"
                                placeholder="Acme Inc."
                                value={formData.companyName}
                                onChange={(e) => updateData({ companyName: e.target.value })}
                                error={!!errors.companyName}
                                helperText={errors.companyName}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Industry"
                                placeholder="EdTech"
                                value={formData.industry}
                                onChange={(e) => updateData({ industry: e.target.value })}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Website"
                                placeholder="acme.com"
                                value={formData.website}
                                onChange={(e) => updateData({ website: e.target.value })}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="What does your company do?"
                                placeholder="Products, audience, tone of voice…"
                                value={formData.description}
                                onChange={(e) => updateData({ description: e.target.value })}
                                fullWidth
                                multiline
                                rows={3}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Footer */}
            <Box display="flex" justifyContent="flex-end" mt={4} pt={2.5} borderTop="1px solid" borderColor="grey.100">
                <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleNext}
                    disableElevation
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, px: 3 }}
                >
                    Next: Business Category
                </Button>
            </Box>
        </Box>
    );
}